import React, { FC } from "react";
import {
  Box,
  Chip,
  MenuItem,
  Select,
  SelectProps,
  Tooltip,
} from "@mui/material";
import { isEmpty } from "lodash";
import { useAppTheme } from "../dark-theme";
import { RouteState } from "service";

export interface ControlledLabelSelectOption {
  label: string | React.ReactNode;
  value: string;
  disabled?: boolean;
  status?: string;
  tooltip?: string;
}

export interface ControlledLabelSelectProps
  extends Omit<SelectProps, "placeholder" | "onChange" | "allow"> {
  value?: string;
  placeholder?: string;
  onChange?: (value: string) => void;
  options: ControlledLabelSelectOption[];
  placeholderSelectEnabled?: boolean;
  renderOption?: (
    option: ControlledLabelSelectOption,
    layoutStyles: React.CSSProperties,
  ) => React.ReactNode;
  renderSelected?: (
    selectedOption: ControlledLabelSelectOption | undefined,
    layoutStyles: React.CSSProperties,
  ) => React.ReactNode;
  layoutStyles?: React.CSSProperties;
  sxProp?: React.CSSProperties;
  placeholdeShow?: boolean;
  placeholderSelectLabel?: string;
}

// Updated to use theme colors instead of hardcoded values
export const getStatusColor = (status: string) => {
  const theme = useAppTheme();
  const { palette } = theme;

  switch (status) {
    case RouteState.PLANNING:
      return "#FFA500"; // Keep original orange for planning
    case RouteState.PLANNED:
      return palette.routeStates.planned; // Use theme color for planned
    case RouteState.LIVE:
      return palette.routeStates.live; // Use theme color for live
    case RouteState.ENDED:
      return palette.routeStates.ended; // Use theme color for ended
    default:
      return "#D3D3D3";
  }
};

// Keeping the old map for backward compatibility, but using theme colors
export const statusColorMap: { [key: string]: string } = {
  Planning: "#FFA500",
  Planned: "#58B4EF",
  Live: "#3fbf9e",
  Ended: "#8F9193",
};

export const ControlledLabelSelect: FC<ControlledLabelSelectProps> = (
  props,
) => {
  const {
    value,
    placeholder,
    onChange,
    options,
    placeholderSelectEnabled = true,
    placeholderSelectLabel = "All",
    placeholdeShow = true,
    renderOption,
    layoutStyles,
    renderSelected,
    sxProp,
    ...rest
  } = props;

  return (
    <Select
      {...rest}
      displayEmpty
      disableUnderline
      size="small"
      variant="filled"
      value={isEmpty(value) ? "none" : value}
      onChange={(e) => onChange?.(e.target.value as string)}
      renderValue={(selected) => {
        if (selected === "none") {
          return placeholder;
        }
        const selectedOption = options.find(
          (option) => option.value === selected,
        );
        return renderSelected ? (
          renderSelected(selectedOption, layoutStyles ? layoutStyles : {})
        ) : (
          <>sds</>
        );
      }}
    >
      <MenuItem
        value="none"
        disabled={!placeholderSelectEnabled}
        sx={placeholdeShow ? {} : { display: "none" }}
      >
        {placeholderSelectLabel}
      </MenuItem>
      {options.map((option) => {
        const menuItem = (
          <MenuItem key={option.value} value={option.value}>
            {renderOption ? (
              renderOption(option, layoutStyles ? layoutStyles : {})
            ) : (
              <StatusChip
                option={option}
                layoutStyles={layoutStyles}
                sxProp={sxProp}
              />
            )}
          </MenuItem>
        );

        return option.tooltip ? (
          <Tooltip key={option.value} title={option.tooltip} placement="right">
            {menuItem}
          </Tooltip>
        ) : (
          menuItem
        );
      })}
    </Select>
  );
};

// Extracted component for better organization
const StatusChip: FC<{
  option: ControlledLabelSelectOption;
  layoutStyles?: React.CSSProperties;
  sxProp?: React.CSSProperties;
}> = ({ option, layoutStyles, sxProp }) => {
  return (
    <Box sx={{ display: "flex", alignItems: "center", ...layoutStyles }}>
      {option.label}
      {option.status && (
        <Chip
          label={option.status}
          sx={{
            backgroundColor: getStatusColor(option.status),
            color: "#fff",
            fontWeight: "bold",
            marginLeft: 1,
            ...sxProp,
          }}
          size="small"
        />
      )}
    </Box>
  );
};
